import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { FeeTypeRow, OrgFeeTypesApi } from '@/services/fee-types.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select } from '@/components/ui/Input';

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/** Org tier — Fees Type, cross-school. Pick a school to manage its fee types, or create one across every school in scope at once (the real functional equivalent of legacy's "Apply to: All Schools"). */
export function OrgFeeTypesPage() {
  const qc = useQueryClient();
  const [schoolId, setSchoolId] = useState('');
  const [modal, setModal] = useState<{ open: boolean; row?: FeeTypeRow }>({ open: false });
  const [confirm, setConfirm] = useState<{ open: boolean; row?: FeeTypeRow }>({ open: false });

  const { data: schools = [] } = useQuery({ queryKey: ['org-fee-types-schools'], queryFn: () => OrgFeeTypesApi.schools() });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['org-fee-types', schoolId],
    queryFn: () => OrgFeeTypesApi.list(schoolId),
    enabled: !!schoolId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['org-fee-types'] });

  const save = useMutation({
    mutationFn: async (v: { id?: string; applyToAll: boolean; data: { name: string; description?: string } }): Promise<void> => {
      if (v.id) await OrgFeeTypesApi.update(v.id, schoolId, v.data);
      else await OrgFeeTypesApi.create({ ...v.data, schoolId: v.applyToAll ? 'all' : schoolId });
    },
    onSuccess: () => {
      invalidate();
      setModal({ open: false });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => OrgFeeTypesApi.remove(id, schoolId),
    onSuccess: () => {
      invalidate();
      setConfirm({ open: false });
    },
  });

  return (
    <>
      <PageHeader
        title="Fees Type"
        description="Fee category labels per school. Pick a school to manage its list, or create a fee type across every school in your organization at once."
        actions={
          <button className="btn-primary" onClick={() => setModal({ open: true })} disabled={!schoolId}>
            <Plus className="mr-1.5 h-4 w-4" /> Add fee type
          </button>
        }
      />

      <div className="mb-4">
        <Select className="w-64" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">— Select a school —</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      {!schoolId ? (
        <div className="card p-8 text-center text-sm text-slate-400">Select a school to view its fee types.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Name</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Description</th>
                <th className="px-4 py-2 text-right font-medium text-slate-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-800">{r.name}</td>
                  <td className="px-4 py-2 text-slate-600">{r.description ?? '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <span className="inline-flex items-center gap-2">
                      <button type="button" title="Edit" className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700" onClick={() => setModal({ open: true, row: r })}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button type="button" title="Delete" className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600" onClick={() => setConfirm({ open: true, row: r })}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-sm text-slate-400">
                    No fee types yet for this school.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <FeeTypeFormModal
        open={modal.open}
        row={modal.row}
        saving={save.isPending}
        errorMsg={errMsg(save.error)}
        onClose={() => setModal({ open: false })}
        onSubmit={(v) => save.mutate({ id: modal.row?.id, applyToAll: v.applyToAll, data: { name: v.name, description: v.description } })}
      />

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.row && remove.mutate(confirm.row.id)}
        loading={remove.isPending}
        title="Delete fee type?"
        message={`Remove "${confirm.row?.name}" from this school. This cannot be undone.`}
        confirmText="Delete"
      />
    </>
  );
}

function FeeTypeFormModal({
  open,
  row,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  open: boolean;
  row?: FeeTypeRow;
  onClose: () => void;
  onSubmit: (v: { name: string; description?: string; applyToAll: boolean }) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ name: string; description: string; applyToAll: boolean }>({
    values: { name: row?.name ?? '', description: row?.description ?? '', applyToAll: false },
  });

  useEffect(() => {
    if (open) reset({ name: row?.name ?? '', description: row?.description ?? '', applyToAll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={row ? `Edit ${row.name}` : 'Add fee type'}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={saving}
            onClick={handleSubmit((v) => onSubmit({ name: v.name, description: v.description || undefined, applyToAll: v.applyToAll }))}
          >
            {saving ? 'Saving…' : row ? 'Save changes' : 'Add'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" required error={errors.name?.message}>
          <Input {...register('name', { required: 'Required' })} />
        </Field>
        <Field label="Description">
          <Input {...register('description')} />
        </Field>
        {!row && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" {...register('applyToAll')} />
            Apply to every school in my organization
          </label>
        )}
        {errorMsg && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
      </div>
    </Modal>
  );
}
